import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { verifyPermission } from "helpers/permission";
import { verifyTokenExpire } from "middleware/verifyToken";
import { formatSelectOption, localize } from "helpers/function";
import { ToastBottomEnd } from "views/components/Toast";
import {
  toastAddErrorMessageConfig,
  toastAddSuccessMessageConfig,
  toastErrorMessageWithParameterConfig,
  toastSuccessMessageWithParameterConfig,
} from "config/toastConfig";
import appConfig from "config/appConfig";
import i18n from "i18n";
import FileSaver from "file-saver";

const endPointConfig = {
  PRO: {
    plan: "PRO",
    list: `${appConfig.apiDomaine}/my/processing-circuits`,
    downloadExcel: () => `${appConfig.apiDomaine}/my/processing-circuits/download/excel`,
  },
  MACRO: {
    holding: {
      list: `${appConfig.apiDomaine}/any/processing-circuits`,
      downloadExcel: (institution) => `${appConfig.apiDomaine}/any/processing-circuits/${institution}/download/excel`,
    },
    filial: {
      list: `${appConfig.apiDomaine}/my/processing-circuits`,
      downloadExcel: () => `${appConfig.apiDomaine}/my/processing-circuits/download/excel`,
    },
  },
  HUB: {
    plan: "HUB",
    list: `${appConfig.apiDomaine}/without-institution/processing-circuits`,
    downloadExcel: (institution) => `${appConfig.apiDomaine}/any/processing-circuits/${institution}/download/excel`,
  },
};

const defaultData = {
  institution_id: [],
};

export const useProcessingCircuitData = (plan, userPermissions) => {
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState({});
  const [unitTypes, setUnitTypes] = useState([]);
  const [claimObject, setClaimObject] = useState([]);
  const [data, setData] = useState(undefined);
  const [claimAutoTransferEnabled, setClaimAutoTransferEnabled] = useState(false);
  const [institutionId, setInstitutionId] = useState(undefined);
  const [error] = useState(defaultData);
  const [startRequest, setStartRequest] = useState(false);
  const [institutionData, setInstitutionData] = useState(undefined);
  const [institution, setInstitution] = useState(null);

  const getEndpoint = useCallback(() => {
    let endPoint = "";
    if (plan === "MACRO") {
      if (
        verifyPermission(
          userPermissions,
          "update-processing-circuit-any-institution"
        )
      ) {
        endPoint = endPointConfig[plan].holding;
      } else if (
        verifyPermission(
          userPermissions,
          "update-processing-circuit-my-institution"
        )
      ) {
        endPoint = endPointConfig[plan].filial;
      }
    } else {
      endPoint = endPointConfig[plan];
    }
    return endPoint;
  }, [plan, userPermissions]);

  useEffect(() => {
    const fetchData = async () => {
      if (!verifyTokenExpire()) return;

      const endPoint = getEndpoint();

      try {
        setLoading(true);

        if (
          verifyPermission(
            userPermissions,
            "update-processing-circuit-any-institution"
          )
        ) {
          const institutionResponse = await axios.get(endPoint.list);
          const options = institutionResponse.data.institutions.length
            ? institutionResponse.data.institutions.map((institution) => ({
                value: institution.id,
                label: institution.name,
              }))
            : [];
          setInstitutionData([options]);
        }

        const response = await axios.get(endPoint.list);

        let newObjectData = [];
        response.data.claimCategories.forEach((claimCategory) =>
          claimCategory.claim_objects.forEach(
            (claimObject) =>
              (newObjectData[claimObject.id] = claimObject.units.map(
                (unit) => ({
                  value: unit.id,
                  label: localize(unit.name),
                })
              ))
          )
        );
        setClaimAutoTransferEnabled(response.data.claimAutoTransferEnabled);
        setUnitTypes(response.data.unitTypes);
        setUnits(response.data.units);
        setData(newObjectData);
        setClaimObject(response.data.claimCategories);
        setInstitutionId(response.data.institution_id);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getEndpoint, userPermissions]);

  useEffect(() => {
    for(const claimObjectId in data){
        if(data[claimObjectId].length){
          updateSelectedUnitTypes(claimObjectId, data[claimObjectId])
        }
    }

  }, [data]);

  
  const onChangeUnitTypes = (selected, objectId) => {
      
    let all = false;

    const newUnitTypes = selected ? selected
        .map((sel) => {
          if (sel.value !== "all") {
            return { value: sel.value, label: sel.label };
          } else if (sel.value === "all") {
            all = true;
            return null;
          }
          return sel;
        })
        .filter(Boolean)
    : [];

    if(all && newUnitTypes.length){
      newUnitTypes = formatSelectOption(unitTypes, "name", "fr")
    }

    if(selectedUnitTypes && selectedUnitTypes[objectId] && selectedUnitTypes[objectId].length > newUnitTypes.length){//retrait de type d'unité
      const removedUnitTypes = selectedUnitTypes[objectId].filter(
        (prevType) => !newUnitTypes.some((newType) => newType.value === prevType.value)
      );

      const unitsToRemove = units.filter((unit) => removedUnitTypes.some((unitType) => unitType.value === unit.unit_type_id)).map((unit) => ({
        value: unit.id,
        label: localize(unit.name),
      }));

      setData((prevData) => {
          const newUnits = prevData[objectId].filter(
            (unit) => !unitsToRemove.some((toRemove) => toRemove.value === unit.value)
          );
          return {
            ...prevData,
            [objectId]: newUnits,
          };
      });
    }else{//type d'unité ajouté
      const newUnits = units.filter((unit) => newUnitTypes.some((unitType) => unitType.value === unit.unit_type_id)).map((unit) => ({
        value: unit.id,
        label: localize(unit.name),
      }));

      setSelectedUnitTypes((prevSelectedUnitTypes) => ({ 
        ...prevSelectedUnitTypes, 
        [objectId]: newUnitTypes 
      }));

      setData((prevData) => {
        const merged = [...(prevData[objectId] || []), ...newUnits];

        const uniques = Array.from(
          new Map(merged.map(item => [item.value, item])).values()
        );

        return {
          ...prevData,
          [objectId]: uniques,
        };
      });

    }
  }

  const onChangeProcessing = useCallback(
    (selected, objectId) => {
      let all = false;

      const newUnits = selected ? selected
          .map((sel) => {
            if (sel.value !== "all") {
              return { value: sel.value, label: sel.label };
            } else if (sel.value === "all") {
              all = true;
              return null;
            }
            return sel;
          })
          .filter(Boolean)
      : [];

      if(all){
        newUnits = formatSelectOption(units, "name", "fr")
      }

      
      updateSelectedUnitTypes(objectId, newUnits);
      setData((prevData) => ({ ...prevData, [objectId]: newUnits }));
    },
    [units]
  );

  const updateSelectedUnitTypes = (objectId, selectedUnits) => {
    const selectedUnitIds = selectedUnits.map((unit) => unit.value);
    const selectedTypes = [];

    unitTypes.forEach((unitType) => {
      const hasMissingUnit = units.some(
        (unit) =>
          unit.unit_type_id === unitType.id &&
          !selectedUnitIds.includes(unit.id)
      );
      if (hasMissingUnit) {
        //continue
        return;
      }
      selectedTypes.push({ value: unitType.id, label: localize(unitType.name) });
    });
    

    setSelectedUnitTypes((prevSelectedUnitTypes) => ({ 
      ...prevSelectedUnitTypes, 
      [objectId]: selectedTypes 
    }));
  }

  const onChangeClaimAutoTransfer = useCallback(async (e) => {
    e.preventDefault();
    if (!verifyTokenExpire()) return;
    setStartRequest(true);
    try {
      const endPoint = getEndpoint();
      let submitEndpoint = "";
      if (
        verifyPermission(
          userPermissions,
          "update-processing-circuit-any-institution"
        )
      ) {
        submitEndpoint = `${endPoint.list}/${institutionId}`;
      } else {
        submitEndpoint = endPoint.list;
      }
      submitEndpoint += '/claim-auto-transfer-config';
      const isChecked = e.target.checked;
      await axios.put(submitEndpoint, {
        'claimAutoTransferEnabled': Number(isChecked),
      });
      setClaimAutoTransferEnabled(isChecked);
      ToastBottomEnd.fire(toastSuccessMessageWithParameterConfig(`${i18n.t("Auto-affectation des réclamations")} ${isChecked ? "activée" : "désactivée"}`));
    } catch (error) {
      console.error("Error submitting data:", error);
      ToastBottomEnd.fire(toastAddErrorMessageConfig());
    } finally {
      setStartRequest(false);
    }
  }, [institutionId, getEndpoint, userPermissions]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!verifyTokenExpire()) return;

      setStartRequest(true);

      try {
        const endPoint = getEndpoint();
        let claimObjects = { ...data };
        let values = {};

        for (const claimObjectId in claimObjects) {
          let processings = claimObjects[claimObjectId];
          values[claimObjectId] = processings
            ? processings.map((processing) => processing.value)
            : null;
        }

        let submitEndpoint = "";
        if (
          verifyPermission(
            userPermissions,
            "update-processing-circuit-any-institution"
          )
        ) {
          submitEndpoint = `${endPoint.list}/${institutionId}`;
        } else {
          submitEndpoint = endPoint.list;
        }

        await axios.put(submitEndpoint, values);
        ToastBottomEnd.fire(toastAddSuccessMessageConfig());
      } catch (error) {
        console.error("Error submitting data:", error);
        ToastBottomEnd.fire(toastAddErrorMessageConfig());
      } finally {
        setStartRequest(false);
      }
    },
    [data, getEndpoint, institutionId, userPermissions]
  );

  const onChangeInstitution = useCallback(async (selected) => {
    if (!verifyTokenExpire()) return;

    setInstitutionId(selected.value);
    setInstitution(selected);

    try {
      setLoading(true);
      const response = await axios.get(
        `${appConfig.apiDomaine}/any/processing-circuits/${selected.value}`
      );


      let newObjectData = [];
      response.data.claimCategories.forEach((claimCategory) => {
        claimCategory.claim_objects.forEach(
          (claimObject) =>
            (newObjectData[claimObject.id] = claimObject.units.map((unit) => ({
              value: unit.id,
              label: localize(unit.name),
            })))
        );
      });

      
      setClaimAutoTransferEnabled(response.data.claimAutoTransferEnabled);
      setUnitTypes(response.data.unitTypes);
      setUnits(response.data.units ? response.data.units : []);
      setData(newObjectData);
      setClaimObject(response.data.claimCategories);
      // setInstitutionId(response.data.institution_id);
    } catch (error) {
      console.error("Error fetching institution data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  
  const downloadUsersExcel = () => {
    return new Promise((resolve, reject) => {
      if(verifyPermission(
          userPermissions,
          "update-processing-circuit-any-institution"
        ) && !institution){
        ToastBottomEnd.fire(toastErrorMessageWithParameterConfig(i18n.t("Veuillez sélectionner une institution pour télécharger le fichier Excel")));
        reject("Aucune institution sélectionnée")
        return;
      }
      
      axios.get(getEndpoint().downloadExcel(institution?.value), { responseType: "blob" })
      .then((response) => {
        const today = new Date();
        const fileName =`processing_circuits_${today.getDate().toString().padStart(2, '0')}${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getFullYear()}_${today.getHours().toString().padStart(2, '0')}${today.getMinutes().toString().padStart(2, '0')}${today.getSeconds().toString().padStart(2, '0')}.xlsx`
        FileSaver.saveAs(
          response.data,
          fileName
        );
        resolve("success");
      })
      .catch((error) => {
        console.log(error);
        ToastBottomEnd.fire(toastErrorMessageWithParameterConfig(i18n.t("Echec de téléchargement du fichier Excel")));
        reject(error);
      })
      
    });
  }


  return {
    loading,
    claimObject,
    units,
    selectedUnitTypes,
    unitTypes,
    claimAutoTransferEnabled,
    data,
    institutionData,
    institution,
    institutionId,
    error,
    startRequest,
    onChangeProcessing,
    onChangeUnitTypes,
    onChangeClaimAutoTransfer,
    onSubmit,
    onChangeInstitution,
    downloadUsersExcel,
  };
};
